import { useEffect, useMemo, useState } from 'react';
import { plainMessageText } from '../../netlify/functions/lib/messageFormatting.mjs';
import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  type FieldValue,
  type Timestamp,
  type Transaction,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { functionHeaders } from './functionSecurity';
import { useAuth } from './auth-context';
import { notificationDurablyAccepted, notifyUsers } from './notifications';
import type { MessageAttachment } from './threads';
import type { User } from 'firebase/auth';
import { isDancerLoginUserDoc, type UserDoc } from './users';
import { saveAttachmentMessage, saveBagGroupMessage } from './messageAttachmentWrite';
import { bagGroupPostLink, bagGroupPostVersionMs } from './bagGroupBoard';
import { canJoinBagGroup, isDirectorFohOrManager } from './roleCapabilities';
import type { BagGroupAuthorRole } from './roleCapabilities';
import {
  BagGroupEditConflictError,
  bagGroupDetailsStillMatch,
  bagGroupMembersStillMatch,
  cleanBagGroupDetailsInput,
  type BagGroupDetailEditSnapshot,
  type BagGroupItemExpectedFields,
  type BagGroupItemStoredFields,
  type BagGroupMemberEditSnapshot,
} from './bagGroupEditConcurrency';
export { bagGroupItemExpectedFields, type BagGroupItemStoredFields } from './bagGroupEditConcurrency';
// The same honest family-name derivation the Manage Users page already uses
// (its own union-find over students/familyUids, and its own 416-line test
// file) - reused rather than reinvented for the bag group roster's family
// headings. See groupBagGroupHouseholds() in bagGroupHouseholds.ts,
// re-exported below.

export type BagGroupStatus = 'active' | 'archived';
export type BagGroupItemKind = 'checklist' | 'signup';
export type BagGroupItemStatus = 'needed' | 'claimed' | 'dropped-off' | 'done';

/** One dancer actually in this group, on one member's entry. Id and name
 * both ride along (never id-only) because id is what makes cross-dancer
 * grouping and removal exact, and name is what every name-only reader
 * (dancerNames, the message feed, groupMemberSummaryText) has always shown. */
export interface BagGroupMemberDancer {
  id: string;
  name: string;
}

export interface BagGroupMemberSummary {
  uid: string;
  displayName: string;
  dancerNames: string[];
  /**
   * THE DANCERS OF THIS ACCOUNT WHO ARE ACTUALLY IN THIS GROUP (Bobbie,
   * 2026-09-01: bag group membership is PER DANCER, not per parent - the
   * same mom can have one dancer in Bag A and a different dancer in Bag B,
   * and no derived rule can tell you which; it has to be an explicit pick).
   *
   * Present only on an entry written by the dancer picker (memberSummariesForDancers,
   * below). Its absence marks a PRE-PIVOT or staff-added entry, whose
   * dancerNames still means "every dancer this account has" - see
   * groupBagGroupHouseholds() for how the two are told apart on render.
   * Kept in lockstep with dancerNames by every writer in this file:
   * dancerNames is always dancers.map(d => d.name).
   */
  dancers?: BagGroupMemberDancer[];
  /**
   * A comp dancer with NO linked parent account still gets a row (Bobbie:
   * a bag group sends messages, so a dancer nobody can reach must stay
   * visible rather than silently vanish). True only on that synthetic row.
   * uid is a `dancer:<studentId>` placeholder - non-empty (normalizeMemberSummary
   * requires a uid) and, by the colon, unable to collide with a real
   * Firebase Auth uid (the same convention registrations.ts and
   * familyGroups.ts already use for non-account sentinels). Excluded from
   * memberUids by every writer below, so nothing tries to notify or admit a
   * sign-in that cannot exist.
   */
  noAccount?: boolean;
  /**
   * THE GROUP'S LEAD (Bobbie, 2026-08-26: "Each bag group needs a Bag Group
   * Lead that is listed at the top"). More than one is normal - her own
   * setup has two on Bag C.
   *
   * IT LIVES ON THE MEMBER ENTRY, and that is the whole design. A top-level
   * `leadUids` list was built first and the release probe REFUSED it (run
   * 68, 205,079 bytes): shared/firestore.rules validates memberSummaries
   * only as "a list of at most 100" and never inspects an entry, so a flag
   * inside one costs ZERO rules bytes on a file that has no room left. The
   * dancers list and noAccount flag above ride the exact same free spot.
   *
   * It is also the better shape. A lead is structurally a member here -
   * there is no way to name a lead who is not in the list, and removing
   * somebody from the group takes their lead mark with them, so the orphan
   * case a separate uid list would have to defend against cannot happen.
   */
  isLead?: boolean;
  /** Explicit parent membership survives removing their last picked dancer. */
  addedDirectly?: boolean;
}

/**
 * Per-account cap on dancers listed under one bag group member entry.
 *
 * MEASURED, not guessed (review finding: the old cap of 8 silently dropped
 * a family's ninth dancer with nothing telling anyone - "silent data
 * loss... the worst available option," and nine or more comp dancers on
 * one account is rare but not exotic once step and foster families are
 * counted). One {id, name} pair in `dancers`, plus its mirror in
 * `dancerNames`, costs roughly 100 bytes on the wire - a Firestore
 * auto-id runs 20 characters, a full dancer name usually 15 to 30, plus
 * JSON punctuation, twice over for the two arrays. Raising the cap from 8
 * to 24 adds at most 16 * 100 = 1,600 bytes to ONE member entry, and a bag
 * group holds at most 100 entries (hasBagGroupShape in
 * shared/firestore.rules), so the absolute worst case adds roughly 160KB
 * to one group document - far under Firestore's 1MB document ceiling, and
 * its storage cost (Firestore bills by stored bytes, roughly
 * $0.18/GiB/month) rounds to nothing. This is UNRELATED to the rules
 * content-byte budget: that ceiling is the size of shared/firestore.rules
 * itself, not of any document it governs, and this constant does not
 * touch that file.
 *
 * 24 comfortably covers a blended, step, or foster household on the comp
 * team while still bounding the array against a malformed or abusive
 * write.
 */
const MAX_DANCERS_PER_MEMBER = 24;

export interface BagGroupDoc {
  id: string;
  name: string;
  seasonId: string;
  seasonName: string;
  status: BagGroupStatus;
  memberUids: string[];
  memberSummaries: BagGroupMemberSummary[];
  itemCount: number;
  openItemCount: number;
  lastMessageText: string | null;
  lastMessageId: string | null;
  lastMessageAt: Timestamp | FieldValue | null;
  createdAt: Timestamp | FieldValue;
  createdBy: string;
  updatedAt: Timestamp | FieldValue;
  archivedAt?: Timestamp | FieldValue | null;
  archivedBy?: string | null;
}

export interface BagGroupMessageDoc {
  id: string;
  authorUid: string;
  authorName: string;
  /**
   * Who the author was posting AS, checked against their live profile by the
   * Firestore rule in both directions. 'staff' is an Admin or Manager who is
   * a member of this group (Bobbie, 2026-08-25); see canJoinBagGroup().
   */
  authorRole: BagGroupAuthorRole;
  text: string;
  /** Photos, videos, and safe files shared in this message. */
  attachments?: MessageAttachment[];
  createdAt: Timestamp | FieldValue;
  /** Missing on legacy messages, which remain independent original posts. */
  replyToId?: string;
  /** Updated atomically by the reply gateway, never by a browser write. */
  lastReplyAt?: Timestamp | FieldValue;
  replyCount?: number;
  /** Who wrote the latest reply, and the activity before her run of replies (BG-5). */
  lastReplyByUid?: string;
  priorReplyAt?: Timestamp | FieldValue;
  /**
   * Heart and seen, matched to class posts (DECISIONS 123 item 16). Written
   * ONLY by netlify/functions/bagMessageReaction.mjs, one key per person.
   */
  reactions?: Record<string, string>;
  acks?: Record<string, unknown>;
  dismissedBy?: Record<string, unknown>;
  /** The class composer's Acknowledgement required, set by staff at posting. */
  ackRequired?: boolean;
  /** Who the post asked, stamped by the gateway from the members at posting. */
  ackAudienceUids?: string[];
}

export interface BagGroupItemDoc {
  id: string;
  groupId: string;
  kind: BagGroupItemKind;
  title: string;
  note: string;
  attachments?: MessageAttachment[];
  competitionName: string;
  competitionDate: string | null;
  /** Links to countdownEvents/{id} when picked from the Countdowns list, else null (free-text competitionName). */
  countdownEventId: string | null;
  /** 'YYYY-MM-DD' - when this item should be dropped off. Auto-filled to 7 days before a picked event's date, but always editable. */
  dropOffDate: string | null;
  quantity: string;
  dropOffLocation: string;
  ownerUid: string | null;
  ownerName: string | null;
  status: BagGroupItemStatus;
  droppedOffAt: Timestamp | FieldValue | null;
  droppedOffBy: string | null;
  createdAt: Timestamp | FieldValue;
  createdBy: string;
  updatedAt: Timestamp | FieldValue;
  /**
   * The editable text fields exactly as STORED (trimmed, null when missing or
   * not a string), with none of the defaults above ("Item" for a missing
   * title, 'checklist' for a missing kind). What an edit sends as its
   * expectedFields, because the item lane compares those against the stored
   * values (BG-6 final review follow-up 3). Set by normalizeItem; absent on a
   * hand-built item, which then edits in the older unguarded shape.
   */
  stored?: BagGroupItemStoredFields;
}

/**
 * Defensive read of a stored authorRole. An unknown value reads as 'family',
 * the least-privileged label, so a malformed document can never render a
 * parent's message as if the studio sent it.
 */
function normalizeAuthorRole(value: unknown): BagGroupAuthorRole {
  if (value === 'director') return 'director';
  if (value === 'staff') return 'staff';
  return 'family';
}

function cleanString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

function normalizeMemberDancer(value: unknown): BagGroupMemberDancer | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const id = cleanString(raw.id);
  const name = cleanString(raw.name);
  if (!id || !name) return null;
  return { id, name };
}

function normalizeMemberDancers(value: unknown): BagGroupMemberDancer[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out: BagGroupMemberDancer[] = [];
  const seenIds = new Set<string>();
  for (const item of value) {
    const dancer = normalizeMemberDancer(item);
    if (!dancer || seenIds.has(dancer.id)) continue;
    seenIds.add(dancer.id);
    out.push(dancer);
  }
  return out.length > 0 ? out.slice(0, MAX_DANCERS_PER_MEMBER) : undefined;
}

function normalizeMemberSummary(value: unknown): BagGroupMemberSummary | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const uid = cleanString(raw.uid);
  if (!uid) return null;
  const dancers = normalizeMemberDancers(raw.dancers);
  return {
    uid,
    displayName: cleanString(raw.displayName) ?? 'Family member',
    dancerNames: normalizeStringArray(raw.dancerNames).slice(0, MAX_DANCERS_PER_MEMBER),
    // Present only when the picker actually wrote it, same reasoning as
    // isLead just below: an untouched pre-pivot entry stays byte-identical.
    ...(dancers ? { dancers } : {}),
    ...(raw.noAccount === true ? { noAccount: true as const } : {}),
    // Only when true, so a member who is not a lead is byte-identical to one
    // written before this field existed. A normalizer that dropped this would
    // silently make the whole feature invisible, which is exactly what it did
    // on the first pass.
    ...(raw.isLead === true ? { isLead: true as const } : {}),
    ...(raw.addedDirectly === true ? { addedDirectly: true as const } : {}),
  };
}

/** Exported for the render harness (tests/bag-group-items-ui.test.mjs), which feeds it the lane's documents. */
export function normalizeGroup(id: string, value: unknown): BagGroupDoc {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const memberSummaries = Array.isArray(raw.memberSummaries)
    ? raw.memberSummaries
        .map(normalizeMemberSummary)
        .filter((member): member is BagGroupMemberSummary => Boolean(member))
    : [];
  const memberUids = normalizeStringArray(raw.memberUids);
  return {
    id,
    name: cleanString(raw.name) ?? 'Bag Group',
    seasonId: cleanString(raw.seasonId) ?? '',
    seasonName: cleanString(raw.seasonName) ?? 'Season',
    status: raw.status === 'archived' ? 'archived' : 'active',
    memberUids,
    memberSummaries,
    itemCount: typeof raw.itemCount === 'number' ? raw.itemCount : 0,
    openItemCount: typeof raw.openItemCount === 'number' ? raw.openItemCount : 0,
    lastMessageText: cleanString(raw.lastMessageText),
    lastMessageId: cleanString(raw.lastMessageId),
    lastMessageAt: (raw.lastMessageAt as Timestamp | FieldValue | null) ?? null,
    createdAt: raw.createdAt as Timestamp | FieldValue,
    createdBy: cleanString(raw.createdBy) ?? '',
    updatedAt: raw.updatedAt as Timestamp | FieldValue,
    archivedAt: (raw.archivedAt as Timestamp | FieldValue | null) ?? null,
    archivedBy: cleanString(raw.archivedBy),
  };
}

export function normalizeBagGroupMessage(id: string, value: unknown): BagGroupMessageDoc {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  return {
    id,
    authorUid: cleanString(raw.authorUid) ?? '',
    authorName: cleanString(raw.authorName) ?? 'Family member',
    authorRole: normalizeAuthorRole(raw.authorRole),
    text: cleanString(raw.text) ?? '',
    attachments: Array.isArray(raw.attachments)
      ? raw.attachments as MessageAttachment[]
      : undefined,
    createdAt: raw.createdAt as Timestamp | FieldValue,
    ...(typeof raw.replyToId === 'string' && raw.replyToId ? { replyToId: raw.replyToId } : {}),
    ...(raw.lastReplyAt ? { lastReplyAt: raw.lastReplyAt as Timestamp | FieldValue } : {}),
    ...(typeof raw.replyCount === 'number' && Number.isSafeInteger(raw.replyCount) && raw.replyCount >= 0
      ? { replyCount: raw.replyCount } : {}),
    ...(typeof raw.lastReplyByUid === 'string' && raw.lastReplyByUid ? { lastReplyByUid: raw.lastReplyByUid } : {}),
    ...(raw.priorReplyAt ? { priorReplyAt: raw.priorReplyAt as Timestamp | FieldValue } : {}),
    ...(isPlainMap(raw.reactions) ? {
      reactions: Object.fromEntries(Object.entries(raw.reactions).filter(([, emoji]) => typeof emoji === 'string')) as Record<string, string>,
    } : {}),
    ...(isPlainMap(raw.acks) ? { acks: raw.acks } : {}),
    ...(isPlainMap(raw.dismissedBy) ? { dismissedBy: raw.dismissedBy } : {}),
    ...(raw.ackRequired === true ? {
      ackRequired: true,
      ackAudienceUids: Array.isArray(raw.ackAudienceUids)
        ? raw.ackAudienceUids.filter((uid): uid is string => typeof uid === 'string' && uid.length > 0)
        : [],
    } : {}),
  };
}

function isPlainMap(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Exported for the render harness, as normalizeGroup. */
export function normalizeItem(id: string, value: unknown): BagGroupItemDoc {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const status =
    raw.status === 'claimed' || raw.status === 'dropped-off' || raw.status === 'done'
      ? raw.status
      : 'needed';
  return {
    id,
    groupId: cleanString(raw.groupId) ?? '',
    kind: raw.kind === 'signup' ? 'signup' : 'checklist',
    title: cleanString(raw.title) ?? 'Item',
    note: cleanString(raw.note) ?? '',
    attachments: Array.isArray(raw.attachments)
      ? raw.attachments as MessageAttachment[]
      : undefined,
    competitionName: cleanString(raw.competitionName) ?? '',
    competitionDate: cleanString(raw.competitionDate),
    countdownEventId: cleanString(raw.countdownEventId),
    dropOffDate: cleanString(raw.dropOffDate),
    quantity: cleanString(raw.quantity) ?? '',
    dropOffLocation: cleanString(raw.dropOffLocation) ?? '',
    ownerUid: cleanString(raw.ownerUid),
    ownerName: cleanString(raw.ownerName),
    status,
    droppedOffAt: (raw.droppedOffAt as Timestamp | FieldValue | null) ?? null,
    droppedOffBy: cleanString(raw.droppedOffBy),
    createdAt: raw.createdAt as Timestamp | FieldValue,
    createdBy: cleanString(raw.createdBy) ?? '',
    updatedAt: raw.updatedAt as Timestamp | FieldValue,
    stored: {
      kind: cleanString(raw.kind),
      title: cleanString(raw.title),
      note: cleanString(raw.note),
      competitionName: cleanString(raw.competitionName),
      competitionDate: cleanString(raw.competitionDate),
      countdownEventId: cleanString(raw.countdownEventId),
      dropOffDate: cleanString(raw.dropOffDate),
      quantity: cleanString(raw.quantity),
      dropOffLocation: cleanString(raw.dropOffLocation),
    },
  };
}

/**
 * The item as stored right now, read from the server (not the cache), for
 * putting its current values back into Edit item after the lane answered
 * 'item_changed'. Null when it is gone or cannot be read.
 */
export async function fetchBagGroupItemFresh(groupId: string, itemId: string): Promise<BagGroupItemDoc | null> {
  if (!db) return null;
  try {
    const snap = await getDocFromServer(doc(db, 'bagGroups', groupId, 'items', itemId));
    return snap.exists() ? normalizeItem(snap.id, snap.data()) : null;
  } catch {
    return null;
  }
}

export function displayNameForUser(user: Pick<UserDoc, 'displayName' | 'email' | 'uid'>): string {
  return user.displayName || 'A family';
}

export interface UseBagGroupsResult {
  loading: boolean;
  groups: BagGroupDoc[];
  error: string | null;
  /** Whose groups `groups` is: the uid of the read that last settled, or null. */
  settledForUid: string | null;
}

export function useBagGroupsForUser(userDoc: UserDoc | null | undefined): UseBagGroupsResult {
  const [groups, setGroups] = useState<BagGroupDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settledForUid, setSettledForUid] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !userDoc || userDoc.status !== 'active') {
      setGroups([]);
      setLoading(false);
      setError(null);
      setSettledForUid(null);
      return;
    }
    setLoading(true);
    // THE LIST TRAP: the unfiltered query is only provable for the roles
    // canReadBagGroup() admits without a membership check. Manager joined that
    // rule 2026-08-13; if this line and the rule ever disagree, the whole
    // query is denied and the page renders as though there were no groups.
    const q =
      isDirectorFohOrManager(userDoc.role)
        ? query(collection(db, 'bagGroups'))
        : query(
            collection(db, 'bagGroups'),
            where('status', '==', 'active'),
            where('memberUids', 'array-contains', userDoc.uid)
          );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map((d) => normalizeGroup(d.id, d.data()));
        docs.sort((a, b) => {
          if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
          const season = b.seasonName.localeCompare(a.seasonName);
          if (season !== 0) return season;
          return a.name.localeCompare(b.name);
        });
        setGroups(docs);
        setError(null);
        setLoading(false);
        setSettledForUid(userDoc.uid);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
        setSettledForUid(userDoc.uid);
      }
    );
    return () => unsub();
  }, [userDoc?.role, userDoc?.status, userDoc?.uid]);

  return { loading, groups, error, settledForUid };
}

export function useBagGroup(groupId: string | undefined): {
  loading: boolean;
  group: BagGroupDoc | null;
  error: string | null;
} {
  const [group, setGroup] = useState<BagGroupDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !groupId) {
      setGroup(null);
      setLoading(false);
      return;
    }
    // Clear and go back to loading whenever the effect re-runs. The detail
    // page keeps the same route element across a groupId change (browser
    // back/forward between two groups), so without this the previous group's
    // header renders as settled under the new group's URL until the snapshot
    // lands. Same reset in all three bagGroups hooks.
    setGroup(null);
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, 'bagGroups', groupId),
      (snap) => {
        setGroup(snap.exists() ? normalizeGroup(snap.id, snap.data()) : null);
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [groupId]);

  return { loading, group, error };
}

export function useBagGroupMessages(
  groupId: string | undefined,
  enabled = true,
  focusMessageId?: string,
  viewerUid?: string
): {
  loading: boolean;
  messages: BagGroupMessageDoc[];
  error: string | null;
  hasOlder: boolean;
  loadingOlder: boolean;
  loadOlder: () => Promise<void>;
} {
  const scope = JSON.stringify([groupId, viewerUid, enabled]);
  const [window, setWindow] = useState({ scope, size: 200 });
  const size = window.scope === scope ? window.size : 200;
  const [state, setState] = useState({
    scope, messages: [] as BagGroupMessageDoc[], loading: true,
    error: null as string | null, hasOlder: false, loadingOlder: false,
  });

  useEffect(() => {
    let live = true;
    let revision = 0;
    if (!enabled || !db || !groupId) {
      setState({ scope, messages: [], loading: false, error: null, hasOlder: false, loadingOlder: false });
      return;
    }
    const firestore = db;
    setState((current) => current.scope === scope ? { ...current, loading: current.messages.length === 0 }
      : { scope, messages: [], loading: true, error: null, hasOlder: false, loadingOlder: false });
    const q = query(
      collection(firestore, 'bagGroups', groupId, 'messages'),
      orderBy('createdAt', 'desc'),
      limit(size)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const currentRevision = ++revision;
        // Keep the existing newest window. Hydrate only the originals needed
        // by replies in that window (and the exact linked post), so old roots
        // cannot disappear when a new reply is the 201st message.
        void (async () => {
          const byId = new Map(snap.docs.map((d) => [d.id, normalizeBagGroupMessage(d.id, d.data())]));
          let incomplete = false;
          const readExact = async (messageId: string) => {
            if (!messageId || messageId.includes('/')) { incomplete = true; return; }
            try {
              const message = await getDoc(doc(firestore, 'bagGroups', groupId, 'messages', messageId));
              if (message.exists()) byId.set(message.id, normalizeBagGroupMessage(message.id, message.data()));
              else incomplete = true;
            } catch { incomplete = true; }
          };
          if (focusMessageId && !byId.has(focusMessageId)) await readExact(focusMessageId);
          const missing = [...new Set([...byId.values()].flatMap((message) =>
            message.replyToId && !byId.has(message.replyToId) ? [message.replyToId] : []))];
          await Promise.all(missing.map(readExact));
          if (!live || currentRevision !== revision) return;
          const messages = [...byId.values()].sort((a, b) =>
            bagGroupPostVersionMs({ ...a, lastReplyAt: undefined }) - bagGroupPostVersionMs({ ...b, lastReplyAt: undefined })
            || a.id.localeCompare(b.id));
          setState({ scope, messages, loading: false, loadingOlder: false, hasOlder: snap.size === size,
            error: incomplete ? 'Some earlier posts could not be loaded. Refresh and try again.' : null });
        })();
      },
      (err) => {
        revision += 1;
        if (live) setState({ scope, messages: [], loading: false, loadingOlder: false, hasOlder: false, error: err.message });
      }
    );
    return () => { live = false; unsub(); };
  }, [enabled, groupId, focusMessageId, scope, size]);

  const loadOlder = async () => {
    setState((current) => current.scope === scope ? { ...current, loadingOlder: true } : current);
    setWindow({ scope, size: size + 200 });
  };
  if (state.scope !== scope) return { loading: enabled, messages: [], error: null, hasOlder: false, loadingOlder: false, loadOlder };
  return { ...state, loadOlder };
}

/** One open post only, newest replies first even after years of group history. */
export function useBagGroupReplies(
  groupId: string | undefined, postId: string | undefined, enabled = true, viewerUid?: string
): { replies: BagGroupMessageDoc[]; loading: boolean; error: string | null; hasMore: boolean; loadMore: () => void } {
  const scope = JSON.stringify([groupId, postId, enabled, viewerUid]);
  const [window, setWindow] = useState({ scope, size: 200 });
  const size = window.scope === scope ? window.size : 200;
  const [state, setState] = useState({ scope, replies: [] as BagGroupMessageDoc[], loading: true, error: null as string | null, hasMore: false });
  useEffect(() => {
    if (!db || !enabled || !groupId || !postId) {
      setState({ scope, replies: [], loading: false, error: null, hasMore: false });
      return;
    }
    let live = true;
    setState((current) => current.scope === scope ? { ...current, loading: true }
      : { scope, replies: [], loading: true, error: null, hasMore: false });
    const unsub = onSnapshot(query(collection(db, 'bagGroups', groupId, 'messages'),
      where('replyToId', '==', postId), orderBy('createdAt', 'desc'), limit(size)), (snap) => {
      if (!live) return;
      const replies = snap.docs.map((d) => normalizeBagGroupMessage(d.id, d.data())).reverse();
      setState({ scope, replies, loading: false, error: null, hasMore: snap.size === size });
    }, (error) => {
      if (live) setState({ scope, replies: [], loading: false, error: error.message, hasMore: false });
    });
    return () => { live = false; unsub(); };
  }, [groupId, postId, enabled, scope, size]);
  const loadMore = () => setWindow({ scope, size: size + 200 });
  if (state.scope !== scope) return { replies: [], loading: enabled, error: null, hasMore: false, loadMore };
  return { ...state, loadMore };
}

export function useBagGroupItems(groupId: string | undefined): {
  loading: boolean;
  items: BagGroupItemDoc[];
  error: string | null;
} {
  const [items, setItems] = useState<BagGroupItemDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !groupId) {
      setItems([]);
      setLoading(false);
      return;
    }
    // See useBagGroup: reset on every re-run so a groupId change never shows
    // the previous group's packing list as settled under the new group.
    setItems([]);
    setLoading(true);
    const q = query(collection(db, 'bagGroups', groupId, 'items'), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map((d) => normalizeItem(d.id, d.data())));
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [groupId]);

  return { loading, items, error };
}

/**
 * The names to print at the top of a bag group (Bobbie, 2026-08-26: "Each
 * bag group needs a Bag Group Lead that is listed at the top").
 *
 * Resolved from memberSummaries by uid, never stored separately: that is
 * what stops a lead's name drifting from the member list, and it means the
 * email-as-name rule is already satisfied because memberSummaries carries
 * the scrubbed display name.
 */
export function bagGroupLeadNames(group: BagGroupDoc): string[] {
  return group.memberSummaries
    .filter((member) => member.isLead === true)
    .map((member) => member.displayName)
    .filter((name) => Boolean(name && name.trim()));
}

/** "Lead" for one, "Leads" for several - Bag C has two. */
export function bagGroupLeadLabel(names: readonly string[]): string {
  return names.length === 1 ? 'Lead' : 'Leads';
}

export function groupMemberSummaryText(group: BagGroupDoc): string {
  if (group.memberSummaries.length === 0) return 'No members assigned yet';
  return group.memberSummaries
    .map((member) =>
      member.dancerNames.length > 0
        ? `${member.displayName} (${member.dancerNames.join(', ')})`
        : member.displayName
    )
    .join(', ');
}

/**
 * Real, signable-in account uids only - a noAccount row's `dancer:<id>`
 * placeholder can never match a real auth.uid, so leaving it out of
 * memberUids just keeps that array honest (nothing to notify, nothing that
 * could ever pass isBagGroupMember in the rules).
 *
 * WHY THAT FILTER IS THE ONLY THING STOPPING IT, ON RECORD PER REVIEW: if
 * some future write path forgot this filter and a `dancer:<id>` string
 * slipped into memberUids anyway, isBagGroupMember (shared/firestore.rules)
 * would treat it as a full read grant on paper - `request.auth.uid in
 * data.memberUids` doesn't know or care what the string looks like. It is
 * unreachable in practice, not because a rule stops it, but because a real
 * Firebase Auth uid can never contain a colon, so no signed-in caller's
 * request.auth.uid could ever equal it. That is client-side discipline,
 * not a rules-enforced backstop - a shape guard on memberUids entries is
 * not free at 0 rules-content-byte budget - so this filter has to stay
 * live on every write path rather than being treated as a formality.
 */
function memberUidsOf(members: readonly BagGroupMemberSummary[]): string[] {
  return members.filter((member) => member.noAccount !== true).map((member) => member.uid);
}

/** Every dancer the picker put in these entries, by id. */
function pickedDancerIdsOf(members: readonly unknown[]): string[] {
  const ids = new Set<string>();
  for (const member of members) {
    const dancers = member && typeof member === 'object' ? (member as { dancers?: unknown }).dancers : null;
    if (!Array.isArray(dancers)) continue;
    for (const dancer of dancers) {
      const id = dancer && typeof dancer === 'object' ? (dancer as { id?: unknown }).id : null;
      if (typeof id === 'string' && id.trim()) ids.add(id.trim());
    }
  }
  return [...ids];
}

/** The membership lane's per-call dancer cap. */
export const BAG_GROUP_ANNOUNCE_DANCER_CAP = 100;

/**
 * THE ANNOUNCE CALLS ONE SAVE NEEDS (bag group plan BG-3, final review
 * follow-up 2). The membership lane (netlify/functions/bagGroupMembers.mjs)
 * takes at most BAG_GROUP_ANNOUNCE_DANCER_CAP dancers a call, the same cap
 * its BAG_GROUP_MEMBER_CAP holds. A group holds at most 100 member entries
 * but each entry can carry several dancers, so a restored group can carry
 * more dancers than one call takes. Cutting the list at the cap used to leave
 * the rest of the group's linked parents uncaught; this pages it instead.
 *
 * The people the save named ride on the first call only, so nobody is told
 * twice. Pure, so it is tested on its own. The cap is the lane's
 * BAG_GROUP_MEMBER_CAP (tests/bag-group-membership-sync.test.mjs pins them equal).
 */
export function bagGroupAnnounceBatches(
  uids: readonly string[],
  dancerIds: readonly string[],
  cap: number = BAG_GROUP_ANNOUNCE_DANCER_CAP
): Array<{ uids: string[]; dancerIds: string[] }> {
  const people = [...new Set(uids)].filter(Boolean);
  const dancers = [...new Set(dancerIds)].filter(Boolean);
  if (people.length === 0 && dancers.length === 0) return [];
  const size = Math.max(1, Math.floor(cap));
  const batches: Array<{ uids: string[]; dancerIds: string[] }> = [];
  for (let start = 0; start === 0 || start < dancers.length; start += size) {
    batches.push({ uids: start === 0 ? people : [], dancerIds: dancers.slice(start, start + size) });
  }
  return batches;
}

/**
 * BEING ADDED SAYS SO (Bobbie, 2026-10-08, DECISIONS 126 item 2, "Bell +
 * email (Recommended)": "You are in {group}. Your lead is {name}."). Staff add
 * people to a group from these screens with the Director and Admin rules
 * write below; once that write has committed, the server lane
 * (netlify/functions/bagGroupMembers.mjs) tells each person just added, once,
 * with one bell card and one email. The server re-checks every uid against
 * the live group, never tells the same person about the same group twice, and
 * never tells the person who made the change, so this sends only what changed.
 *
 * PICKED DANCERS ride along. This screen builds a picked dancer's parents
 * from the family links it last loaded, so a parent linked while the save was
 * in flight would be missed. The lane re-reads each picked dancer's links
 * after this write has committed and adds anyone missing to this group, so
 * "picking a dancer adds every parent linked to them" holds even then.
 *
 * Best effort by design: the membership is saved, and a missed alert must
 * never read as a failed save. A failure is logged for whoever looks next.
 */
async function announceNewBagGroupMembers(
  groupId: string,
  uids: readonly string[],
  pickedDancerIds: readonly string[] = []
): Promise<void> {
  const me = auth?.currentUser;
  const fresh = [...new Set(uids)].filter((uid) => uid && uid !== me?.uid);
  // A group can carry more dancers than one call takes, so the dancers go in
  // pages of the lane's cap; the people named ride on the first page only.
  const batches = bagGroupAnnounceBatches(fresh, pickedDancerIds);
  if (!me || batches.length === 0) return;
  for (const batch of batches) {
    try {
      const response = await fetch('/.netlify/functions/bagGroupMembers', {
        method: 'POST',
        headers: await functionHeaders(me),
        body: JSON.stringify({ action: 'announce', groupId, uids: batch.uids, dancerIds: batch.dancerIds }),
      });
      if (!response.ok) {
        console.warn('Bag group welcome alert was not sent', response.status);
        return;
      }
    } catch (error) {
      console.warn('Bag group welcome alert was not sent', error);
      return;
    }
  }
}

export function useCreateBagGroup() {
  const { user } = useAuth();
  return async (input: {
    name: string;
    seasonId: string;
    seasonName: string;
    memberSummaries: BagGroupMemberSummary[];
  }): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const name = input.name.trim();
    if (!name) throw new Error('Bag Group name is required.');
    if (!input.seasonId) throw new Error('Choose a season first.');
    const members = uniqueMemberSummaries(input.memberSummaries);
    if (members.length > 100) throw new Error('A Bag Group can hold up to 100 people. Remove someone before adding more.');
    const ref = doc(collection(db, 'bagGroups'));
    let added: string[] = [];
    let pickedDancerIds: string[] = [];
    await runTransaction(db, async (tx) => {
      if (auth?.currentUser?.uid !== user.uid) throw new Error('Sign in again before creating a Bag Group.');
      const selectedParents = members.filter((member) => member.addedDirectly).map((member) => member.uid);
      const parents = await readBagGroupParentMembers(tx, selectedParents);
      const currentMembers = uniqueMemberSummaries([...members, ...parents]);
      added = memberUidsOf(currentMembers);
      pickedDancerIds = pickedDancerIdsOf(currentMembers);
      if (auth?.currentUser?.uid !== user.uid) throw new Error('Sign in again before creating a Bag Group.');
      tx.set(ref, {
        name,
        seasonId: input.seasonId,
        seasonName: input.seasonName,
        status: 'active' as const,
        memberUids: memberUidsOf(currentMembers),
        memberSummaries: currentMembers,
        itemCount: 0,
        openItemCount: 0,
        lastMessageText: null,
        lastMessageId: null,
        lastMessageAt: null,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        updatedAt: serverTimestamp(),
        archivedAt: null,
        archivedBy: null,
      });
    });
    await announceNewBagGroupMembers(ref.id, added, pickedDancerIds);
    return ref.id;
  };
}

function mergeMemberDancers(
  a: BagGroupMemberDancer[] | undefined,
  b: BagGroupMemberDancer[] | undefined
): BagGroupMemberDancer[] | undefined {
  if (!a && !b) return undefined;
  const byId = new Map<string, BagGroupMemberDancer>();
  for (const dancer of [...(a ?? []), ...(b ?? [])]) {
    const id = dancer.id.trim();
    const name = dancer.name.trim();
    if (!id || !name || byId.has(id)) continue;
    byId.set(id, { id, name });
  }
  return byId.size > 0 ? [...byId.values()].slice(0, MAX_DANCERS_PER_MEMBER) : undefined;
}

/**
 * Dedupe by uid, UNIONING rather than overwriting when the same uid shows
 * up twice in the input - which happens on purpose now: adding a second
 * selected dancer who shares a parent with an already-selected one produces
 * two entries for that one parent uid, and they need to merge into one
 * member carrying both dancers, not clobber each other. Also the one place
 * that used to silently drop isLead, dancers and noAccount on every
 * add/remove-member write (every caller routes through here) - fixed
 * forward since this pass already touches every field on this path.
 */
export function uniqueMemberSummaries(members: BagGroupMemberSummary[]): BagGroupMemberSummary[] {
  const byUid = new Map<string, BagGroupMemberSummary>();
  for (const member of members) {
    if (!member.uid) continue;
    const existing = byUid.get(member.uid);
    const dancers = mergeMemberDancers(existing?.dancers, member.dancers);
    const dancerNames = dancers
      ? dancers.map((dancer) => dancer.name)
      : Array.from(
          new Set(
            [...(existing?.dancerNames ?? []), ...member.dancerNames]
              .map((name) => name.trim())
              .filter(Boolean)
          )
        ).slice(0, MAX_DANCERS_PER_MEMBER);
    byUid.set(member.uid, {
      uid: member.uid,
      displayName: member.displayName.trim() || existing?.displayName || 'Family member',
      dancerNames,
      ...(dancers ? { dancers } : {}),
      ...(member.noAccount === true || existing?.noAccount === true ? { noAccount: true as const } : {}),
      ...(member.isLead === true || existing?.isLead === true ? { isLead: true as const } : {}),
      ...(member.addedDirectly === true || existing?.addedDirectly === true ? { addedDirectly: true as const } : {}),
    });
  }
  return [...byUid.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/** One dancer available to pick for a bag group - just enough to resolve
 * both the display and the parents to attach it to. */
export interface BagGroupDancerCandidate {
  id: string;
  name: string;
  familyUids: string[];
}

/**
 * Turn a set of SELECTED DANCERS into member entries (Bobbie, 2026-09-01:
 * "we need to be able to select the dancers" - membership is per dancer,
 * not per parent). Each selected dancer contributes itself to every one of
 * its own familyUids' entries, merging automatically (via
 * uniqueMemberSummaries) when two selected dancers share a parent, and gets
 * its own no-account row when it has no linked family account at all -
 * see the noAccount field for why that row still has to exist.
 */
export function memberSummariesForDancers(
  dancers: readonly BagGroupDancerCandidate[],
  parentDisplayName: (uid: string) => string | null
): BagGroupMemberSummary[] {
  const entries: BagGroupMemberSummary[] = [];
  for (const dancer of dancers) {
    const id = dancer.id.trim();
    const name = dancer.name.trim();
    if (!id || !name) continue;
    const familyUids = Array.from(new Set(dancer.familyUids.map((uid) => uid.trim()).filter(Boolean)));
    if (familyUids.length === 0) {
      entries.push({
        uid: `dancer:${id}`,
        displayName: name,
        dancerNames: [name],
        dancers: [{ id, name }],
        noAccount: true,
      });
      continue;
    }
    for (const uid of familyUids) {
      entries.push({
        uid,
        displayName: parentDisplayName(uid) ?? 'Family member',
        dancerNames: [name],
        dancers: [{ id, name }],
      });
    }
  }
  return uniqueMemberSummaries(entries);
}

/**
 * Drop one dancer from every member entry that carries it. An entry the
 * dancer picker wrote (it has a `dancers` list) disappears once its last
 * dancer is gone - it was only in the group because of that dancer. An
 * entry with no `dancers` list (a pre-pivot member, or a staff lead added
 * directly, never scoped to a dancer in the first place) is untouched.
 */
export function removeDancerFromMemberSummaries(
  members: readonly BagGroupMemberSummary[],
  dancerId: string
): BagGroupMemberSummary[] {
  const next: BagGroupMemberSummary[] = [];
  for (const member of members) {
    if (!member.dancers) {
      next.push(member);
      continue;
    }
    const dancers = member.dancers.filter((dancer) => dancer.id !== dancerId);
    if (dancers.length === 0) {
      if (member.addedDirectly) {
        const { dancers: _removed, ...directMember } = member;
        next.push({ ...directMember, dancerNames: [] });
      }
      continue;
    }
    next.push({ ...member, dancers, dancerNames: dancers.map((dancer) => dancer.name) });
  }
  return next;
}

export {
  bagGroupChartRows,
  bagGroupDancerCount,
  bagGroupDefaultQuantity,
  bagGroupHeroCounts,
  bagGroupHeroCountsText,
  groupBagGroupHouseholds,
  sharesBagGroupHousehold,
  type BagGroupChartRow,
  type BagGroupHousehold,
} from './bagGroupHouseholds';

export async function updateBagGroupMembers(
  groupId: string,
  memberSummaries: BagGroupMemberSummary[],
  expected: BagGroupMemberEditSnapshot
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const members = uniqueMemberSummaries(memberSummaries);
  if (members.length > 100) {
    throw new Error('A Bag Group can hold up to 100 people. Remove someone before adding more.');
  }
  const groupRef = doc(db, 'bagGroups', groupId);
  let added: string[] = [];
  let pickedDancerIds: string[] = [];
  await runTransaction(db, async (tx) => {
    const current = await tx.get(groupRef);
    if (!current.exists()) throw new Error('Bag Group not found.');
    if (!bagGroupMembersStillMatch(current.data(), expected)) {
      throw new BagGroupEditConflictError();
    }
    const currentData = current.data() as Record<string, unknown>;
    const before = new Set(normalizeStringArray(currentData.memberUids));
    added = memberUidsOf(members).filter((uid) => !before.has(uid));
    const dancersBefore = new Set(pickedDancerIdsOf(
      Array.isArray(currentData.memberSummaries) ? currentData.memberSummaries : []
    ));
    pickedDancerIds = pickedDancerIdsOf(members).filter((id) => !dancersBefore.has(id));
    tx.update(groupRef, {
      memberUids: memberUidsOf(members),
      memberSummaries: members,
      updatedAt: serverTimestamp(),
    });
  });
  await announceNewBagGroupMembers(groupId, added, pickedDancerIds);
  await syncBagGroupTodos(groupId);
}

async function readBagGroupParentMembers(tx: Transaction, uids: readonly string[]): Promise<BagGroupMemberSummary[]> {
  if (!db) throw new Error('Firestore not initialized.');
  if (uids.length > 100 || uids.some((uid) => !uid || uid.trim() !== uid || uid.includes('/'))) throw new Error('Review the selected parents and try again.');
  const profiles = await Promise.all(uids.map((uid) => tx.get(doc(db!, 'users', uid))));
  return profiles.map((profile, index): BagGroupMemberSummary => {
    const user = profile.exists() ? profile.data() as UserDoc : null;
    if (!user || user.status !== 'active' || !canJoinBagGroup(user.role) || isDancerLoginUserDoc(user)) {
      throw new Error('A selected parent is no longer available. Review the parent list and try again.');
    }
    return { uid: uids[index], displayName: displayNameForUser(user), dancerNames: [], addedDirectly: true };
  });
}

/** Append exact parent accounts to the current document, preserving a
 * concurrent lead/dancer edit instead of replacing a stale screen snapshot. */
export async function addBagGroupParents(groupId: string, parentUids: readonly string[], actorUid: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  if (!actorUid || auth?.currentUser?.uid !== actorUid) throw new Error('Sign in again before adding parents.');
  const uids = [...new Set(parentUids)];
  if (!uids.length) throw new Error('Choose a parent first.');
  if (uids.length > 100 || uids.some((uid) => !uid || uid.trim() !== uid || uid.includes('/'))) throw new Error('Review the selected parents and try again.');
  const groupRef = doc(db, 'bagGroups', groupId);
  let added: string[] = [];
  await runTransaction(db, async (tx) => {
    const current = await tx.get(groupRef);
    if (!current.exists()) throw new Error('Bag Group not found.');
    const group = normalizeGroup(current.id, current.data());
    if (group.status !== 'active') throw new Error('Restore this Bag Group before adding parents.');
    const additions = await readBagGroupParentMembers(tx, uids);
    const existingUids = new Set(group.memberSummaries.map((member) => member.uid));
    const members = uniqueMemberSummaries([...group.memberSummaries, ...additions.filter((member) => !existingUids.has(member.uid))]);
    const memberUids = [...new Set([...group.memberUids, ...memberUidsOf(members)])];
    if (members.length > 100 || memberUids.length > 100) throw new Error('A Bag Group can hold up to 100 people. Remove someone before adding more.');
    if (auth?.currentUser?.uid !== actorUid) throw new Error('Sign in again before adding parents.');
    added = memberUids.filter((uid) => !group.memberUids.includes(uid));
    tx.update(groupRef, { memberUids, memberSummaries: members, updatedAt: serverTimestamp() });
  });
  await announceNewBagGroupMembers(groupId, added);
}

/** Remove only the explicit membership. A picked dancer still keeps their
 * parent in the group, and every other member comes from the current document. */
export async function removeBagGroupDirectParent(groupId: string, parentUid: string, actorUid: string, confirmedLead: boolean): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  if (!actorUid || auth?.currentUser?.uid !== actorUid) throw new Error('Sign in again before removing a parent.');
  const groupRef = doc(db, 'bagGroups', groupId);
  await runTransaction(db, async (tx) => {
    const current = await tx.get(groupRef);
    if (!current.exists()) throw new Error('Bag Group not found.');
    const group = normalizeGroup(current.id, current.data());
    if (group.status !== 'active') throw new Error('Restore this Bag Group before changing parents.');
    const parent = group.memberSummaries.find((member) => member.uid === parentUid);
    if (!parent?.addedDirectly) return;
    if ((parent.isLead === true) !== confirmedLead) throw new BagGroupEditConflictError();
    const keepThroughDancer = !!parent.dancers?.length || parent.dancerNames.length > 0;
    const members = group.memberSummaries.flatMap((member) => {
      if (member.uid !== parentUid) return [member];
      if (!keepThroughDancer) return [];
      const { addedDirectly: _removed, ...linkedParent } = member;
      return [linkedParent];
    });
    const memberUids = keepThroughDancer ? group.memberUids : group.memberUids.filter((uid) => uid !== parentUid);
    if (auth?.currentUser?.uid !== actorUid) throw new Error('Sign in again before removing a parent.');
    tx.update(groupRef, { memberUids, memberSummaries: members, updatedAt: serverTimestamp() });
  });
  await syncBagGroupTodos(groupId);
}

/**
 * Rename a group or move it to another season. Directors, Admin and Managers,
 * on the existing rules path. NAMING THE LEADS IS NOT HERE ANY MORE: it moved
 * to the leads lane (setBagGroupLeads below, netlify/functions/bagGroupLeads.mjs)
 * so a newly named lead is told (Bobbie, 2026-08-26, ruling 7). That lane
 * rewrites the isLead flags from the LIVE member list exactly as this function
 * used to.
 */
export async function updateBagGroupDetails(
  groupId: string,
  input: Pick<BagGroupDoc, 'name' | 'seasonId' | 'seasonName'>,
  expected: BagGroupDetailEditSnapshot
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  // The same checks the edit panel runs before it saves anything.
  const { name, seasonId, seasonName } = cleanBagGroupDetailsInput(input);
  const groupRef = doc(db, 'bagGroups', groupId);
  await runTransaction(db, async (tx) => {
    const current = await tx.get(groupRef);
    if (!current.exists()) throw new Error('Bag Group not found.');
    if (!bagGroupDetailsStillMatch(current.data(), expected)) {
      throw new BagGroupEditConflictError();
    }
    tx.update(groupRef, {
      name,
      seasonId,
      seasonName,
      updatedAt: serverTimestamp(),
    });
  });
}

export async function updateBagGroupStatus(
  groupId: string,
  status: BagGroupStatus,
  actorUid: string
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const archivedBy = actorUid.trim();
  if (!archivedBy) throw new Error('Sign in required.');
  const patch =
    status === 'archived'
      ? { status, archivedAt: serverTimestamp(), archivedBy, updatedAt: serverTimestamp() }
      : { status, archivedAt: null, archivedBy: null, updatedAt: serverTimestamp() };
  await updateDoc(doc(db, 'bagGroups', groupId), patch);
  if (status === 'active') await catchUpRestoredBagGroup(groupId);
  else await syncBagGroupTodos(groupId);
}

/**
 * THE GROUP'S TO-DOS CATCH UP (BG-8, refute round 1, major 1). Archiving a
 * group, or taking someone out of it on these staff screens, is a browser
 * write; once it has committed, the server lane
 * (netlify/functions/bagGroupTodoSweep.mjs) deletes or narrows the bag item
 * to-dos nobody could close any more, from the LIVE group. Best effort, like
 * the alerts: the change is saved whatever happens here, and the To-dos
 * surfaces hide a bag to-do whose group the viewer can no longer open
 * (liveBagItemTaskFilter) in the meantime.
 */
export async function syncBagGroupTodos(groupId: string): Promise<void> {
  try {
    await postBagGroupLane('bagGroupTodoSweep', { groupId });
  } catch (error) {
    console.warn('Bag group to-dos did not catch up', error);
  }
}

/**
 * RESTORING A GROUP CATCHES UP (bag group plan BG-3, refute round P3). The
 * sync that runs when a parent is linked looks only at active groups, so a
 * parent linked to one of this group's dancers while it sat archived was
 * passed over. Once the restore has committed, every dancer the group carries
 * goes to the same lane a fresh pick uses: a linked parent who is missing
 * joins and is told once, and everyone already in the group is left exactly
 * as they are. Best effort, like every alert on these screens: the restore is
 * saved whatever happens here.
 */
async function catchUpRestoredBagGroup(groupId: string): Promise<void> {
  if (!db) return;
  try {
    const snap = await getDoc(doc(db, 'bagGroups', groupId));
    const summaries = snap.exists() ? (snap.data() as Record<string, unknown>).memberSummaries : null;
    await announceNewBagGroupMembers(groupId, [], pickedDancerIdsOf(Array.isArray(summaries) ? summaries : []));
  } catch (error) {
    console.warn('Restored bag group did not catch up its linked parents', error);
  }
}

export interface BagGroupMessageSendResult {
  messageId: string;
  /** The message is saved, but its alert request still needs a durable acknowledgement. */
  alertsPending: boolean;
  /** This device could retain the exact source id for a later Retry alerts tap. */
  recoveryStored: boolean;
}

export async function sendBagGroupMessage(input: {
  group: BagGroupDoc;
  messageId?: string;
  replyToId?: string;
  text: string;
  attachments?: MessageAttachment[];
  authorUid: string;
  authorName: string;
  authorRole: BagGroupAuthorRole;
  /** Staff only, original posts only; the gateway refuses anything else. */
  ackRequired?: boolean;
}): Promise<BagGroupMessageSendResult> {
  if (!db) throw new Error('Firestore not initialized.');
  const text = input.text.trim();
  const attachments = input.attachments ?? [];
  if (!text && attachments.length === 0) throw new Error('Message cannot be empty.');
  // A formatted reply is stored marked: the 4,000 is the words she can see.
  if (plainMessageText(text).length > 4000 || text.length > 250000) throw new Error('Keep your post to 4,000 characters or fewer.');
  const messageRef = input.messageId
    ? doc(db, 'bagGroups', input.group.id, 'messages', input.messageId)
    : doc(collection(db, 'bagGroups', input.group.id, 'messages'));
  const user = auth?.currentUser;
  if (!user || user.uid !== input.authorUid) throw new Error('Sign in required.');
  await saveBagGroupMessage({
    user,
    targetId: input.group.id,
    writeId: messageRef.id,
    attachments,
    payload: {
      text,
      ...(input.replyToId ? { replyToId: input.replyToId } : {}),
      ...(input.ackRequired && !input.replyToId ? { ackRequired: true } : {}),
    },
  });
  /* OFF-APP DELIVERY ON. Reported through /feedback, 2026-09-03: "I am not
     getting any notifications when people post to green tote group b. Others
     in my group and not getting them either." Bobbie, 2026-09-04: "Parents
     and admin should receive notifications about a bag group they are in.
     About every post."

     The source has committed above. Remember its exact id BEFORE the HTTP
     attempt, then retire this record only when the durable alert outbox says
     it accepted the source. A timeout therefore cannot turn into a false
     "sent" message, and Retry alerts never creates another wall post. */
  const delivery = await notifyUsers(
    [],
    {
      kind: 'bag-group',
      title: 'New bag group message',
      body: 'Open the bag group to read it.',
      link: bagGroupPostLink(input.group.id, messageRef.id, input.replyToId),
    },
    { bagGroup: { id: input.group.id, messageId: messageRef.id } }
  );
  return {
    messageId: messageRef.id,
    alertsPending: !notificationDurablyAccepted(delivery),
    recoveryStored: delivery?.recoveryStored !== false,
  };
}

export function newBagGroupMessageId(groupId: string): string {
  if (!db) throw new Error('Firestore not initialized.');
  return doc(collection(db, 'bagGroups', groupId, 'messages')).id;
}

/* ------------------------------------------------------------------ *
 * THE ITEM LANE AND THE LEADS LANE (bag group plan v2, packet BG-6).
 *
 * Every change to a bag group's item list goes through one server lane,
 * netlify/functions/bagGroupItems.mjs, which reads the caller's role from
 * users/{uid} and her lead flag from the live group, and decides there.
 * That is what lets a lead run her group fully (Bobbie, 2026-08-26, ruling
 * 6) without spending a single rules byte, and what turns a lost claim race
 * into a plain sentence instead of Firestore's permission text (the #1363
 * final review). Naming the leads goes through bagGroupLeads.mjs, which also
 * tells a newly named lead (ruling 7).
 *
 * Photos and files on an item still go through the attachment gateway
 * (saveAttachmentMessage, its bagGroupItem branch), the one thing it keeps.
 * ------------------------------------------------------------------ */

export type BagGroupItemOp = 'create' | 'edit' | 'delete' | 'claim' | 'unclaim' | 'dropOff' | 'receive' | 'assign';

/** The fields an item edit may change. Owner and status have operations of their own. */
export type BagGroupItemEditableFields = Pick<
  BagGroupItemDoc,
  | 'kind'
  | 'title'
  | 'note'
  | 'competitionName'
  | 'competitionDate'
  | 'countdownEventId'
  | 'dropOffDate'
  | 'quantity'
  | 'dropOffLocation'
>;

export interface BagGroupItemLaneResult {
  op: BagGroupItemOp;
  itemId: string;
  status: BagGroupItemStatus | null;
  ownerUid: string | null;
  /** False when the item was already in the asked-for state. */
  changed: boolean;
  /** True when this exact request had already run and its first answer came back. */
  replayed: boolean;
}

/**
 * Plain words for every answer the two lanes can refuse with. Nothing raw
 * from Firestore or the server ever reaches the screen.
 */
const BAG_GROUP_LANE_MESSAGES: Record<string, string> = {
  already_claimed: 'Someone else just signed up to bring this one. Pick another item, or check with your lead.',
  not_open: 'This item is not open for sign-up anymore.',
  not_claimed: 'Nobody is signed up for this item right now, so there is nothing to change.',
  not_member: 'Only people in this bag group can sign up to bring something.',
  not_allowed: "Only this group's lead, Admin, Managers and Directors can do that.",
  dancer_sign_in: 'This sign-in can read the bag group. Changes to its list come from a parent or the studio.',
  group_archived: 'This bag group is archived, so its list is closed.',
  group_not_found: 'This bag group could not be found. Go back and open it again.',
  item_not_found: 'That item is gone. Someone may have just removed it.',
  item_exists: 'That item was already added.',
  owner_not_member: 'That person is not in this bag group anymore. Pick someone else.',
  owner_changed: 'Someone else changed who brings this just now, so your pick was not saved. Take another look and pick again.',
  item_checked_in: 'This item was already dropped off or received, so who brings it stays as it is.',
  item_changed: 'Someone else changed this item just now, so your edit was not saved. Take another look and try again.',
  // Every lane that answers it (items, leads, families) means the same thing:
  // this exact tap was already saved, with different details than this retry.
  request_conflict: 'That was already saved with different details. Check the page and try again.',
  title_required: 'Add an item name first.',
  too_long: 'One of those fields is too long. Shorten it and try again.',
  bad_date: 'Check the dates and try again.',
  // Neutral on purpose (BG-6 final review follow-up 2): after her own save
  // lost its answer, the change the lane saw may be hers, not someone else's.
  leads_changed: 'The leads changed since you opened this, so yours were not saved; take another look and try again.',
  lead_not_allowed: "A dancer's own sign-in cannot lead a bag group. Pick a parent instead.",
  group_closed: 'This bag group is archived, so nobody can be added or removed.',
  dancer_not_found: 'That dancer is not on the comp team list anymore. Close the list, open it again and pick again.',
  group_full: 'This bag group already holds 100 people. Remove someone before adding more.',
  member_dancer_limit: 'One of those parents already has the most dancers one person can have in a group, so nothing was added. Ask Admin, a Manager or a Director.',
  would_remove_lead: "Removing this dancer would take a lead out of the group, so nothing changed. Ask Admin, a Manager or a Director to change the leads first.",
  would_remove_staff: 'Removing this dancer would take a studio staff member out of the group, so nothing changed. Ask Admin, a Manager or a Director.',
  too_many_requests: 'That was a lot of changes at once. Wait a moment and try again.',
  director_only: 'Only a Director can run this cleanup.',
  sign_in_required: 'Sign in again, then try that once more.',
  invalid_token: 'Sign in again, then try that once more.',
  'missing app check token': 'This device could not be verified. Refresh and try again.',
  'invalid app check token': 'This device could not be verified. Refresh and try again.',
  server_not_configured: 'The studio server is not set up for this yet.',
};
const BAG_GROUP_LANE_FALLBACK = 'That did not save. Check your connection and try again.';

/** A refusal from a bag group lane, carrying its code and its plain words. */
export class BagGroupLaneRequestError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number) {
    super(BAG_GROUP_LANE_MESSAGES[code] ?? BAG_GROUP_LANE_FALLBACK);
    this.name = 'BagGroupLaneRequestError';
    this.code = code;
    this.status = status;
  }
}

/** The plain line for one lane answer code. Exported for the tests. */
export function bagGroupLaneMessage(code: string): string {
  return BAG_GROUP_LANE_MESSAGES[code] ?? BAG_GROUP_LANE_FALLBACK;
}

/** A fresh id for one tap, so a retry of that tap is recognised as the same request. */
export function newBagGroupLaneRequestId(): string {
  const random = globalThis.crypto?.randomUUID?.();
  if (random) return random;
  if (!db) throw new Error('Firestore not initialized.');
  return doc(collection(db, 'bagGroups')).id;
}

async function postBagGroupLane(
  lane: 'bagGroupItems' | 'bagGroupLeads' | 'bagGroupFamilies' | 'bagGroupTodoSweep' | 'bagGroupDancerCleanup',
  body: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const user = auth?.currentUser;
  if (!user) throw new Error('Sign in required.');
  let response: Response;
  try {
    response = await fetch(`/.netlify/functions/${lane}`, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: await functionHeaders(user),
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error(BAG_GROUP_LANE_FALLBACK);
  }
  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok || payload.ok !== true) {
    throw new BagGroupLaneRequestError(typeof payload.error === 'string' ? payload.error : '', response.status);
  }
  return payload;
}

/** One item operation through the item lane. */
export async function runBagGroupItemOperation(input: {
  op: BagGroupItemOp;
  groupId: string;
  itemId: string;
  requestId?: string;
  fields?: Partial<BagGroupItemEditableFields>;
  ownerUid?: string | null;
  /** assign only: who the screen showed as bringing it (null for nobody). */
  expectedOwnerUid?: string | null;
  /** edit only: the STORED value of each field it changes (bagGroupItemExpectedFields). */
  expectedFields?: BagGroupItemExpectedFields;
}): Promise<BagGroupItemLaneResult> {
  const payload = await postBagGroupLane('bagGroupItems', {
    op: input.op,
    groupId: input.groupId,
    itemId: input.itemId,
    requestId: input.requestId ?? newBagGroupLaneRequestId(),
    ...(input.fields !== undefined ? { fields: input.fields } : {}),
    ...(input.ownerUid !== undefined ? { ownerUid: input.ownerUid } : {}),
    ...(input.expectedOwnerUid !== undefined ? { expectedOwnerUid: input.expectedOwnerUid } : {}),
    ...(input.expectedFields !== undefined ? { expectedFields: input.expectedFields } : {}),
  });
  return payload as unknown as BagGroupItemLaneResult;
}

/**
 * The item was saved with its photos, but handing it to the chosen person did
 * not go through. Thrown so the form can clear (the item exists) and still say
 * what is left to do.
 */
export class BagGroupItemAddedUnassignedError extends Error {
  itemId: string;
  constructor(itemId: string, reason: string) {
    super(`The item was added, but it is not assigned yet. ${reason} Tap Edit item on it to choose who brings it.`);
    this.name = 'BagGroupItemAddedUnassignedError';
    this.itemId = itemId;
  }
}

export function useCreateBagGroupItem() {
  const { user } = useAuth();
  return async (
    groupId: string,
    input: {
      id?: string;
      kind: BagGroupItemKind;
      title: string;
      note?: string;
      competitionName?: string;
      competitionDate?: string | null;
      countdownEventId?: string | null;
      dropOffDate?: string | null;
      quantity?: string;
      dropOffLocation?: string;
      ownerUid?: string | null;
      attachments?: MessageAttachment[];
    }
  ): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const title = input.title.trim();
    if (!title) throw new Error('Add an item name first.');
    const itemId = input.id ?? newBagGroupItemId(groupId);
    const fields: BagGroupItemEditableFields = {
      kind: input.kind,
      title,
      note: input.note?.trim() ?? '',
      competitionName: input.competitionName?.trim() ?? '',
      competitionDate: input.competitionDate || null,
      countdownEventId: input.countdownEventId || null,
      dropOffDate: input.dropOffDate || null,
      quantity: input.quantity?.trim() ?? '',
      dropOffLocation: input.dropOffLocation?.trim() ?? '',
    };
    const ownerUid = input.ownerUid ?? null;
    if (input.attachments?.length) {
      // The gateway saves a new item with its photos for any member, owned by
      // nobody or by the person adding it. Handing it to somebody else is the
      // item lane's assign, which knows who leads the group.
      const gatewayOwner = ownerUid === user.uid ? ownerUid : null;
      await saveAttachmentMessage({
        user,
        action: 'bagGroupItem',
        targetId: groupId,
        writeId: itemId,
        payload: { ...fields, ownerUid: gatewayOwner },
        attachments: input.attachments,
      });
      if (ownerUid && ownerUid !== gatewayOwner) {
        try {
          // The gateway just saved it owned by nobody, so that is what it hands over from.
          await runBagGroupItemOperation({
            op: 'assign', groupId, itemId, requestId: itemId, ownerUid, expectedOwnerUid: gatewayOwner,
          });
        } catch (error) {
          throw new BagGroupItemAddedUnassignedError(itemId, error instanceof Error ? error.message : '');
        }
      }
      return itemId;
    }
    // The draft's own item id doubles as the request id, so a second tap on
    // the same draft replays the first save instead of adding a twin.
    await runBagGroupItemOperation({ op: 'create', groupId, itemId, requestId: itemId, fields, ownerUid });
    return itemId;
  };
}

export function newBagGroupItemId(groupId: string): string {
  if (!db) throw new Error('Firestore not initialized.');
  return doc(collection(db, 'bagGroups', groupId, 'items')).id;
}

/**
 * Change an item's own fields. The lead and studio staff only (the lane
 * decides). With `expectedFields` (bagGroupItemExpectedFields) the lane
 * refuses 'item_changed' when one of those fields changed since Edit item
 * opened, instead of writing over it.
 */
export async function updateBagGroupItem(
  groupId: string,
  itemId: string,
  fields: Partial<BagGroupItemEditableFields>,
  expectedFields?: BagGroupItemExpectedFields,
): Promise<void> {
  await runBagGroupItemOperation({
    op: 'edit', groupId, itemId, fields, ...(expectedFields ? { expectedFields } : {}),
  });
}

/** Append secure uploads while editing a bag item. Existing attachments are
 * preserved by the server and count toward the shared maximum of four. The
 * attachment gateway lets Directors, Admin and Managers append to an item
 * that already exists; a lead adds photos when she creates the item. */
export async function updateBagGroupItemWithAttachments(
  user: User,
  groupId: string,
  item: BagGroupItemDoc,
  fields: Pick<BagGroupItemDoc,
    'kind' | 'title' | 'note' | 'competitionName' | 'competitionDate'
    | 'countdownEventId' | 'dropOffDate' | 'quantity' | 'dropOffLocation'
    | 'ownerUid' | 'ownerName'>,
  attachments: MessageAttachment[],
): Promise<void> {
  await saveAttachmentMessage({
    user,
    action: 'bagGroupItem',
    targetId: groupId,
    writeId: item.id,
    payload: fields,
    attachments,
  });
}

/** "I will bring this": the caller takes an open item. */
export async function claimBagGroupItem(groupId: string, itemId: string): Promise<void> {
  await runBagGroupItemOperation({ op: 'claim', groupId, itemId });
}

/** Give an item back: the owner, her co-parent, the lead or staff. */
export async function unclaimBagGroupItem(groupId: string, itemId: string): Promise<void> {
  await runBagGroupItemOperation({ op: 'unclaim', groupId, itemId });
}

/** "I dropped it off": the owner or her co-parent (ruling 5), the lead or staff. */
export async function markBagGroupItemDroppedOff(groupId: string, itemId: string): Promise<void> {
  await runBagGroupItemOperation({ op: 'dropOff', groupId, itemId });
}

/** "Mark received": the lead or studio staff check an item in. */
export async function receiveBagGroupItem(groupId: string, itemId: string): Promise<void> {
  await runBagGroupItemOperation({ op: 'receive', groupId, itemId });
}

/**
 * Hand an item to a member of the group, or back to nobody (null).
 * `expectedOwnerUid` is who was bringing it when the form opened (null for
 * nobody): if somebody claimed it, or it changed hands, since then, the lane
 * refuses ('owner_changed') instead of quietly undoing that change. An item
 * already dropped off or received is refused too ('item_checked_in').
 */
export async function assignBagGroupItem(
  groupId: string,
  itemId: string,
  ownerUid: string | null,
  expectedOwnerUid: string | null
): Promise<void> {
  await runBagGroupItemOperation({ op: 'assign', groupId, itemId, ownerUid, expectedOwnerUid });
}

export async function deleteBagGroupItem(groupId: string, itemId: string): Promise<void> {
  await runBagGroupItemOperation({ op: 'delete', groupId, itemId });
}

/**
 * Name the group's leads (Directors, Admin and Managers). `expectedLeadUids`
 * is who the screen showed as lead when it opened, so a save made over
 * somebody else's newer change is refused rather than quietly undoing it.
 * Each newly named lead gets a bell card, and push, email and text by her own
 * preferences (Bobbie, 2026-08-26, ruling 7).
 */
export async function setBagGroupLeads(
  groupId: string,
  leadUids: readonly string[],
  expectedLeadUids: readonly string[],
  /** Send the same id again to retry the same save: the lane then answers
   * with its first result instead of judging the retry as a new change. */
  requestId: string = newBagGroupLaneRequestId()
): Promise<{ leadUids: string[]; named: string[] }> {
  const payload = await postBagGroupLane('bagGroupLeads', {
    groupId,
    requestId,
    leadUids: [...new Set(leadUids)],
    expectedLeadUids: [...new Set(expectedLeadUids)],
  });
  return {
    leadUids: Array.isArray(payload.leadUids) ? payload.leadUids as string[] : [],
    named: Array.isArray(payload.named) ? payload.named as string[] : [],
  };
}

export function useBagGroupItemCounts(items: BagGroupItemDoc[]): {
  total: number;
  open: number;
  dropped: number;
} {
  return useMemo(
    () => ({
      total: items.length,
      open: items.filter((item) => item.status === 'needed' || item.status === 'claimed').length,
      dropped: items.filter((item) => item.status === 'dropped-off' || item.status === 'done').length,
    }),
    [items]
  );
}

/* ------------------------------------------------------------------ *
 * THE FAMILIES LANE (BG-6 follow-up 1; plan v2 section 3.4; Bobbie,
 * 2026-08-26, ruling 6, "RUN THEIR GROUP FULLY"). A group's own lead adds
 * and removes the families in her group through
 * netlify/functions/bagGroupFamilies.mjs, which reads her role and her lead
 * flag live and writes with the Admin SDK. She cannot read the comp roster,
 * so the lane also hands her the picker list: an id and a short name per
 * dancer, nothing else. Staff keep their own path above (updateBagGroupMembers).
 * ------------------------------------------------------------------ */

/** One comp dancer a lead may add: an id and the short name she reads. */
export interface BagGroupFamilyChoice {
  id: string;
  name: string;
}

export interface BagGroupFamilyAddResult {
  added: string[];
  joined: number;
  noSignIn: number;
  /** Of those, dancers whose linked parents the group may not hold (inactive, pending, a Teacher parent). */
  noParentToAdd?: number;
  told: number;
  changed: boolean;
  replayed: boolean;
}

export interface BagGroupFamilyRemoveResult {
  dancerId: string;
  left: number;
  changed: boolean;
  replayed: boolean;
}

/** The comp dancers this lead may add to her group, from the lane. */
export async function listBagGroupFamilyChoices(groupId: string): Promise<BagGroupFamilyChoice[]> {
  const payload = await postBagGroupLane('bagGroupFamilies', { action: 'list', groupId });
  const rows = Array.isArray(payload.dancers) ? payload.dancers : [];
  return rows.flatMap((row): BagGroupFamilyChoice[] => {
    const value = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    return typeof value.id === 'string' && typeof value.name === 'string' && value.id && value.name
      ? [{ id: value.id, name: value.name }]
      : [];
  });
}

/** Add picked comp dancers, and the parents linked to them, through the lane. */
export async function addBagGroupFamilies(
  groupId: string,
  dancerIds: readonly string[],
  requestId: string = newBagGroupLaneRequestId()
): Promise<BagGroupFamilyAddResult> {
  const payload = await postBagGroupLane('bagGroupFamilies', {
    action: 'add',
    groupId,
    requestId,
    dancerIds: [...new Set(dancerIds)],
  });
  return payload as unknown as BagGroupFamilyAddResult;
}

/** Take one dancer, and any parent here only for her, out of the group through the lane. */
export async function removeBagGroupFamily(
  groupId: string,
  dancerId: string,
  requestId: string = newBagGroupLaneRequestId()
): Promise<BagGroupFamilyRemoveResult> {
  const payload = await postBagGroupLane('bagGroupFamilies', { action: 'remove', groupId, requestId, dancerId });
  return payload as unknown as BagGroupFamilyRemoveResult;
}

/* ------------------------------------------------------------------ *
 * THE ONE-TIME DANCER SIGN-IN CLEANUP (Bobbie, 2026-10-24: "Clean up the
 * data"). netlify/functions/bagGroupDancerCleanup.mjs, Director only: preview
 * lists the old child logins still on bag group member lists, and the Jr
 * Staff sign-ins it keeps; apply takes the child logins off and answers who
 * was removed.
 * ------------------------------------------------------------------ */

/** One listed sign-in, as the cleanup reports it. */
export interface BagGroupCleanupRow {
  groupId: string;
  groupName: string;
  seasonName: string;
  groupStatus: string;
  uid: string;
  loginName: string;
  studentId: string;
  dancerName: string;
  wasLead: boolean;
  /**
   * Why a kept row stays: 'live' her switched-on Jr Staff sign-in, through
   * which her bag posts reach her other sign-in; 'waiting' a designated Jr
   * Staff's sign-in not switched on as staff yet. '' on a removed row.
   */
  keptReason: '' | 'live' | 'waiting';
}

/** A group the cleanup leaves (or would leave) with no lead. */
export interface BagGroupCleanupNoLead {
  groupId: string;
  groupName: string;
  seasonName: string;
}

export interface BagGroupCleanupPreview {
  toRemove: BagGroupCleanupRow[];
  kept: BagGroupCleanupRow[];
  noLeadGroups: BagGroupCleanupNoLead[];
  scanned: number;
  truncated: boolean;
}

export interface BagGroupCleanupApplyResult {
  removed: BagGroupCleanupRow[];
  kept: BagGroupCleanupRow[];
  noLeadGroups: BagGroupCleanupNoLead[];
  groupsChanged: number;
  todosChanged: number;
  refused: Array<{ groupId: string; groupName: string; reason: string }>;
  scanned: number;
  truncated: boolean;
}

function cleanupNoLead(value: unknown): BagGroupCleanupNoLead[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((row): BagGroupCleanupNoLead[] => {
    const item = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    const str = (key: string) => (typeof item[key] === 'string' ? (item[key] as string) : '');
    return str('groupId') ? [{ groupId: str('groupId'), groupName: str('groupName'), seasonName: str('seasonName') }] : [];
  });
}

function cleanupRows(value: unknown): BagGroupCleanupRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((row): BagGroupCleanupRow[] => {
    const item = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    const str = (key: string) => (typeof item[key] === 'string' ? (item[key] as string) : '');
    if (!str('groupId') || !str('uid')) return [];
    return [{
      groupId: str('groupId'),
      groupName: str('groupName'),
      seasonName: str('seasonName'),
      groupStatus: str('groupStatus'),
      uid: str('uid'),
      loginName: str('loginName'),
      studentId: str('studentId'),
      dancerName: str('dancerName'),
      wasLead: item.wasLead === true,
      keptReason: item.keptReason === 'live' || item.keptReason === 'waiting' ? item.keptReason : '',
    }];
  });
}

/** The preview answer, read defensively. Exported for the browser harness. */
export function bagGroupCleanupPreviewFromPayload(payload: Record<string, unknown>): BagGroupCleanupPreview {
  return {
    toRemove: cleanupRows(payload.toRemove),
    kept: cleanupRows(payload.kept),
    noLeadGroups: cleanupNoLead(payload.noLeadGroups),
    scanned: typeof payload.scanned === 'number' ? payload.scanned : 0,
    truncated: payload.truncated === true,
  };
}

/** The apply answer, read defensively. Exported for the browser harness. */
export function bagGroupCleanupApplyFromPayload(payload: Record<string, unknown>): BagGroupCleanupApplyResult {
  const refused = Array.isArray(payload.refused) ? payload.refused : [];
  return {
    removed: cleanupRows(payload.removed),
    kept: cleanupRows(payload.kept),
    noLeadGroups: cleanupNoLead(payload.noLeadGroups),
    groupsChanged: typeof payload.groupsChanged === 'number' ? payload.groupsChanged : 0,
    todosChanged: typeof payload.todosChanged === 'number' ? payload.todosChanged : 0,
    refused: refused.flatMap((row) => {
      const item = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
      return typeof item.groupId === 'string'
        ? [{ groupId: item.groupId, groupName: typeof item.groupName === 'string' ? item.groupName : '', reason: typeof item.reason === 'string' ? item.reason : '' }]
        : [];
    }),
    scanned: typeof payload.scanned === 'number' ? payload.scanned : 0,
    truncated: payload.truncated === true,
  };
}

/** Who the cleanup would take off, and who it keeps. Reads only. */
export async function previewBagGroupDancerCleanup(): Promise<BagGroupCleanupPreview> {
  return bagGroupCleanupPreviewFromPayload(await postBagGroupLane('bagGroupDancerCleanup', { mode: 'preview' }));
}

/** Take the old dancer sign-ins off, and hear who was removed. */
export async function applyBagGroupDancerCleanup(): Promise<BagGroupCleanupApplyResult> {
  return bagGroupCleanupApplyFromPayload(await postBagGroupLane('bagGroupDancerCleanup', { mode: 'apply' }));
}
